import { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, updateDoc } from 'firebase/firestore';

const MedicalForm = ({ initialData, onSubmit, onCancel }) => {
  const { user, db } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [medical, setMedical] = useState({
    class: initialData?.class || '',
    issueDate: initialData?.issueDate || '',
    expiryDate: initialData?.expiryDate || '',
    restrictions: initialData?.restrictions || '',
    examiner: initialData?.examiner || '',
  });

  const calculateExpiryDate = (issueDate, medicalClass) => {
    if (!issueDate || !medicalClass) return '';
    const date = new Date(issueDate);

    switch (medicalClass) {
      case 'First':
        date.setFullYear(date.getFullYear() + 1);
        break;
      case 'Second':
        date.setFullYear(date.getFullYear() + 2);
        break;
      case 'Third':
        date.setFullYear(date.getFullYear() + 5);
        break;
      case 'BasicMed':
        date.setFullYear(date.getFullYear() + 4);
        break;
      default:
        return '';
    }
    return date.toISOString().split('T')[0];
  };

  const handleClassChange = (value) => {
    setMedical((prev) => ({
      ...prev,
      class: value,
      expiryDate: calculateExpiryDate(prev.issueDate, value),
    }));
  };

  const handleIssueDateChange = (value) => {
    setMedical((prev) => ({
      ...prev,
      issueDate: value,
      expiryDate: calculateExpiryDate(value, prev.class),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!user?.uid || !db) {
        throw new Error('User or database not initialized');
      }

      const pilotRef = doc(db, 'pilot', user.uid);
      await updateDoc(pilotRef, {
        medical,
        'onboarding.medical': true,
        'onboarding.lastUpdated': new Date(),
      });
      onSubmit(medical); // Pass the updated medical data back to the parent
    } catch (err) {
      console.error('Error updating medical:', err);
      setError('Failed to update medical information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div>Saving...</div>;
  if (error) return <div className="text-red-500">{error}</div>;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium">Medical Class</label>
          <select
            value={medical.class}
            onChange={(e) => handleClassChange(e.target.value)}
            className="w-60 px-3 py-2 border rounded-lg"
            required
          >
            <option value="">Select Class</option>
            <option value="First">First Class</option>
            <option value="Second">Second Class</option>
            <option value="Third">Third Class</option>
            <option value="BasicMed">BasicMed</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium">Provider's Name</label>
          <input
            type="text"
            placeholder="AME Name"
            value={medical.examiner}
            onChange={(e) => setMedical({ ...medical, examiner: e.target.value })}
            className="w-60 px-3 py-2 border rounded-lg"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium">Issue Date</label>
          <input
            type="date"
            value={medical.issueDate}
            onChange={(e) => handleIssueDateChange(e.target.value)}
            className="w-60 px-3 py-2 border rounded-lg"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium">Expiration Date</label>
          <input
            type="date"
            value={medical.expiryDate}
            readOnly
            className="w-60 px-3 py-2 border rounded-lg bg-gray-100"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium">Restrictions or Limitations</label>
        <textarea
          placeholder="Enter any restrictions or limitations"
          value={medical.restrictions}
          onChange={(e) => setMedical({ ...medical, restrictions: e.target.value })}
          className="w-full px-3 py-2 border rounded-lg"
          rows={3}
        />
      </div>
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600"
        >
          Save Medical Information
        </button>
      </div>
    </form>
  );
};

export default MedicalForm;
